import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="pt-20 min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-unknow-line text-5xl text-foreground-400 mb-4 block"></i>
          <p className="text-foreground-600 text-lg mb-4">文章未找到</p>
          <Link
            to="/"
            className="inline-block px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const schemaArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '朋友的姐姐',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : '',
    },
  };

  const schemaBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${typeof window !== 'undefined' ? window.location.origin : ''}/` },
      { '@type': 'ListItem', position: 2, name: article.category, item: `${typeof window !== 'undefined' ? window.location.origin : ''}/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: typeof window !== 'undefined' ? window.location.href : '' },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaArticle) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaBreadcrumb) }} />

      <article className="pt-20 min-h-screen bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mt-8 mb-8">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime} 分钟阅读</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="rounded-xl overflow-hidden mb-10 aspect-[16/9]">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
              prose-p:mb-5 prose-p:leading-loose
              prose-strong:text-foreground-900 prose-strong:font-semibold
              [&_p]:text-foreground-800
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-10 border-t border-background-200/70">
              <h2 className="text-xl font-semibold text-foreground-900 mb-6">相关文章</h2>
              <div className="space-y-3">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}